import React, { forwardRef, useImperativeHandle, useRef } from "react";
import FourColumnRowSection from "components/common/FourColumnRowSection/FourColumnRowSection";
import { Field, FieldRef } from "../Field/Field";
import { CustomerRules } from "utils/formRules";
import { ImagePayload } from "types/image.type";
import { SectionProps, StaffState } from "types/staff-edit.types";
import UploadImageButton from "components/common/UploadImageButton/UploadButton";

export interface AccountSectionRef {
  validateAll: () => Promise<boolean>;
}

// 👇 Thêm isEditing vào interface để nhận biết đang Add hay Edit
export interface AccountSectionProps extends SectionProps<StaffState["accountInfo"]> {
  isEditing?: boolean; 
}

export const AccountSection = forwardRef<AccountSectionRef, AccountSectionProps>(
  ({ data, onUpdate, isEditing }, ref) => {
    const nameRef = useRef<FieldRef>(null);
    const passwordRef = useRef<FieldRef>(null);
    const emailRef = useRef<FieldRef>(null);

    useImperativeHandle(ref, () => ({
      validateAll: async () => {
        // Chỉ validate Password nếu KHÔNG PHẢI là Edit (tức là đang Add)
        const promises = [
          nameRef.current?.validate(),
          emailRef.current?.validate(),
        ];
        
        if (!isEditing) {
          promises.push(passwordRef.current?.validate());
        }

        const results = await Promise.all(promises);
        return results.every((r) => r === true);
      },
    }));

    // Bố cục khi đang Edit (Ẩn Password)
    const editRows = [
      {
        leftLabel: "Họ và tên *",
        leftValue: (
          <Field
            ref={nameRef}
            path="accountInfo.fullName"
            value={data?.fullName || ""}
            onUpdate={onUpdate}
            placeholder="Nhập họ và tên..."
            rules={CustomerRules}
          />
        ),
        rightLabel: "Email *",
        rightValue: (
          <Field
            ref={emailRef}
            path="accountInfo.email"
            value={data?.email || ""}
            onUpdate={onUpdate}
            placeholder="example@gmail.com"
            rules={CustomerRules}
          />
        ),
      },
      {
        leftLabel: "Ảnh đại diện",
        leftValue: (
          <div className="flex items-center mt-0 gap-2">
            <UploadImageButton
              label="Tải lên ảnh đại diện"
              isDisabled={false}
              imagesData={data?.avatarImage || []}
              setImagesData={(action) => {
                const currentImages: ImagePayload[] = data?.avatarImage || [];
                const updatedImages = typeof action === "function" ? action(currentImages) : action;
                onUpdate("accountInfo.avatarImage", updatedImages);
              }}
            />
          </div>
        ),
        rightLabel: "",
        rightValue: <></>,
      },
    ];

    // Bố cục khi đang Add (Hiện Password)
    const addRows = [
      {
        leftLabel: "Họ và tên *",
        leftValue: (
          <Field
            ref={nameRef}
            path="accountInfo.fullName"
            value={data?.fullName || ""}
            onUpdate={onUpdate}
            placeholder="Nhập họ và tên..."
            rules={CustomerRules}
          />
        ),
        rightLabel: "Mật khẩu *",
        rightValue: (
          <Field
            ref={passwordRef}
            type="password"
            path="accountInfo.password"
            value={data?.password || ""}
            onUpdate={onUpdate}
            placeholder="Nhập mật khẩu"
            rules={CustomerRules}
          />
        ),
      },
      {
        leftLabel: "Email *",
        leftValue: (
          <Field
            ref={emailRef}
            path="accountInfo.email"
            value={data?.email || ""}
            onUpdate={onUpdate}
            placeholder="example@gmail.com"
            rules={CustomerRules}
          />
        ),
        rightLabel: "Ảnh đại diện",
        rightValue: (
          <div className="flex items-center mt-0 gap-2">
            <UploadImageButton
              label="Tải lên ảnh đại diện"
              isDisabled={false}
              imagesData={data?.avatarImage || []}
              setImagesData={(action) => {
                const currentImages: ImagePayload[] = data?.avatarImage || [];
                const updatedImages = typeof action === "function" ? action(currentImages) : action;
                onUpdate("accountInfo.avatarImage", updatedImages);
              }}
            />
          </div>
        ),
      },
    ];

    return (
      <FourColumnRowSection
        leftTitle="Thông tin tài khoản"
        rows={isEditing ? editRows : addRows}
        rightTitle=""
      />
    );
  }
);

AccountSection.displayName = "AccountSection";